// Global keyboard shortcuts + the "?" help dialog.
import { Dialog } from '@/components/ui/dialog'
import { Kbd } from '@/components/ui/kbd'
import { useHotkeys } from '@/lib/hooks'
import { useSession } from '@/lib/session'
import { permissionFor } from './route-meta'
import { useGuardedNavigate, type ShellKind } from './shell-context'

export const GO_SHORTCUTS: Array<[string, string, string]> = [
  ['g h', 'Home', '/'], ['g o', 'Orders', '/orders'], ['g p', 'Products', '/products'], ['g c', 'Customers', '/customers'],
  ['g d', 'Discounts', '/discounts'], ['g i', 'Inventory', '/inventory'], ['g a', 'Analytics', '/analytics'], ['g s', 'Settings', '/settings'],
]
export const CREATE_SHORTCUTS: Array<[string, string, string]> = [
  ['c p', 'Product', '/products/new'], ['c o', 'Order', '/orders/new'], ['c d', 'Discount', '/discounts/new'], ['c c', 'Collection', '/collections/new'],
]

/** Registers ⌘K, /, ?, [ and the g/c sequences for the store/platform shells. */
export function useShellHotkeys({ kind, openPalette, openShortcuts, toggleSidebar }: { kind: ShellKind; openPalette: () => void; openShortcuts: () => void; toggleSidebar?: () => void }) {
  const go = useGuardedNavigate()
  const { can } = useSession()
  const nav = (to: string) => { const p = permissionFor(to); if (!p || can(p)) void go(to) }
  const store = kind === 'store' || kind === 'full'
  const map: Record<string, (e: KeyboardEvent) => void> = {
    'mod+k': () => openPalette(),
    '/': () => {
      const el = document.querySelector<HTMLInputElement>('[data-page-search]')
      if (el && el.offsetParent !== null) { el.focus(); el.select() } else openPalette()
    },
    '?': () => openShortcuts(),
  }
  if (toggleSidebar) map['['] = () => toggleSidebar()
  if (store) {
    for (const [combo, , to] of GO_SHORTCUTS) map[combo] = () => nav(to)
    for (const [combo, , to] of CREATE_SHORTCUTS) map[combo] = () => nav(to)
  } else if (kind === 'platform') {
    map['g h'] = () => void go('/platform')
    map['g s'] = () => void go('/platform/stores')
    map['g u'] = () => void go('/platform/users')
  }
  useHotkeys(map, {}, [kind, can])
}

const SECTIONS: Array<{ title: string; rows: Array<[string, string]> }> = [
  { title: 'General', rows: [['mod+k', 'Command menu'], ['/', 'Search this page'], ['?', 'Keyboard shortcuts'], ['[', 'Collapse or expand the sidebar'], ['mod+s', 'Save changes'], ['esc', 'Close a dialog or clear search']] },
  { title: 'Go to', rows: GO_SHORTCUTS.map(([k, label]) => [k, label]) },
  { title: 'Create', rows: CREATE_SHORTCUTS.map(([k, label]) => [k, label]) },
  { title: 'Tables', rows: [['j', 'Next row (or ↓)'], ['k', 'Previous row (or ↑)'], ['enter', 'Open row'], ['x', 'Select row (or Space)'], ['mod+enter', 'Open row in a new tab']] },
]

export function ShortcutsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Keyboard shortcuts" description="Work faster without leaving the keyboard. Shortcuts are off while you type in a field." size="lg">
      <div className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
        {SECTIONS.map(s => (
          <section key={s.title} className="flex flex-col gap-1">
            <h3 className="mb-1 text-sm font-semibold text-text-muted">{s.title}</h3>
            {s.rows.map(([combo, label]) => (
              <div key={combo} className="flex h-8 items-center justify-between gap-3 border-b border-border/70 last:border-b-0">
                <span className="text-base text-text">{label}</span>
                <Kbd combo={combo} />
              </div>
            ))}
          </section>
        ))}
      </div>
    </Dialog>
  )
}
